// Contract billability rules for the Billing tab.
//
// What can be invoiced is the contract's call, not the cost code's. On the
// VoltaGrid jobs that call is the rubric the team settled on 9/25/2026
// (Wayne, Berns, Troy and Seth — "Billable Discussion 2026.09.25"), read
// against each job's Service Order:
//
//   54026070 QPAC Blitz     SO Blitz TX3, Execution Version 2026-09-04
//   54026071 Power Block 4  Service Order, Exhibit E, 2026-09-23
//
// Both Service Orders hand the same categories back as reimbursable — welfare
// and temporary facilities, small tools and consumables, the project team and
// supervision, office space, admin support, "all costs associated with the
// use of vehicles", weather-delay minimums — and MEPC ¶1.3.2 ranks a Service
// Order above "all other Exhibits". So the Service Order governs, and MEPC
// Exhibit B only speaks where the Service Order is silent (per diem covering
// lodging and meals, PPE, drug testing, awards). The 9/25 rubric then settles
// the gray areas: safety is never billable, travel is never billable, freight
// rebills at cost and follows the item it delivered, third-party rentals bill
// everything on their invoice, our own equipment's fuel and maintenance are
// inside its wet rate. PB4 differs from Blitz in the per diem threshold (six
// days earn the seventh), the equipment standby minimum (four hours, not five
// or half a day) and the ten-day standby notice.
//
// Each rule carries the language it comes from, because a line moved off the
// invoice — or billed at cost instead of cost-plus — has to be defensible to
// the client. Rules land in four strengths:
//
//   'nb'   not billable. The line defaults to Not Billable with the rule as
//          its reason; overridable per line on Transactions like any other
//          automatic non-billable, and a line already on a summary keeps what
//          it was billed as.
//   'cost' billable at cost, no markup (freight, mob/demob hauling, Strike
//          affiliates). The line prices at 0% instead of its class markup.
//   'warn' billable, but a person has to make a call first (fuel on a rental
//          invoice, a tool that belongs on the rate sheet, a medic the
//          Company has to have approved). Tagged on Transactions.
//   'bill' billable because the contract says so — the Service Order
//          reimburses it, or the rubric does. Changes nothing on the line; it
//          records why the line bills, so the Cost Report can show the whole
//          rubric and not just what came off.
//
// Scope: on for the VoltaGrid jobs by default, off elsewhere — another
// client's contract may well pay for PPE and hotels. Rates & Rules has the
// per-job toggle (settings.ct.on); a job that opts in bills under the newest
// profile (Power Block 4).

// The rubric document the rules were settled in.
export const RUBRIC = {
    name: 'Billable Discussion',
    date: '2026-09-25',
    who: 'Wayne, Berns, Troy, Seth',
    short: '9/25 rubric',
};

// Each VoltaGrid job's governing Service Order and the terms that differ
// between them. `pd` is the per diem policy the job's settings default to
// (billingRates perDiemDays); `standbyHrs` the minimum daily equipment charge
// in hours; `standbyDays` the run of standby business days after which the
// Company has to be offered a demob (null = the SO doesn't ask). `equipAs`
// maps a rate sheet equipment row the SO's schedule doesn't carry to the SO
// row a unit named that way bills at instead (equipRowAs).
export const CONTRACTS = {
    blitz: {
        id: 'blitz',
        jobs: ['54026070'],
        name: 'QPAC Blitz',
        so: 'SO Blitz TX3 (Execution Version, 2026-09-04)',
        soShort: 'SO Blitz',
        pd: { min: 5, full: 7 },
        standbyHrs: 5,
        standbyText: 'five (5) hours or one half (1/2) of the listed daily rate, whichever is greater',
        standbyDays: null,
        equipAs: {},
    },
    pb4: {
        id: 'pb4',
        jobs: ['54026071'],
        name: 'Power Block 4',
        so: 'Service Order — Exhibit E, Power Block 4 (2026-09-23)',
        soShort: 'SO Exh. E',
        pd: { min: 6, full: 7 },
        standbyHrs: 4,
        standbyText: 'four (4) hours of the listed daily rate',
        standbyDays: 10,
        // Attachment B has no Cathodic Protection section, so the 4/6
        // passenger UTVs (PO 231788) bill as its "ATV - 4 Wheel" ($240.88/day)
        // and never at the 8/31 sheet's $200 CP UTV row (Seth, 10/21/26).
        equipAs: { 'UTV / 4x4 side by side / 8x8 amphibious': 'ATV - 4 Wheel' },
    },
};
export const DEFAULT_CONTRACT = 'pb4';

// The jobs a contract governs. Other jobs start with the rules off and can
// opt in from Rates & Rules.
export const MEPC_JOBS = ['54026070', '54026071'];

// The contract profile a job bills under. A job that isn't one of the
// VoltaGrid jobs but has the rules switched on gets the newest one.
export const contractFor = (jobKey) => Object.values(CONTRACTS)
    .find((c) => c.jobs.includes(String(jobKey || ''))) || CONTRACTS[DEFAULT_CONTRACT];

// Rate sheet equipment row -> the row the job's own Service Order bills it
// as. Only for the jobs a Service Order names: the rate sheet is the same for
// everyone, the SO's schedule is not. {} when nothing swaps.
export const equipRowAs = (jobKey) => (Object.values(CONTRACTS)
    .find((c) => c.jobs.includes(String(jobKey || '')))?.equipAs) || {};

export const CONTRACT_SETTINGS_DEFAULT = { on: null };

// null (never set) = on for the VoltaGrid jobs, off for everyone else.
export const contractOn = (settings, jobKey) => {
    const v = settings?.ct?.on;
    if (v === true || v === false) return v;
    return MEPC_JOBS.includes(String(jobKey || ''));
};

// Descriptions compare with punctuation as spaces and runs of whitespace
// collapsed, so 'office trailer' also catches "Office-Trailer" and
// 'mob demob' catches "Mob/Demob".
const norm = (s) => String(s || '').toLowerCase().replace(/[-_/]+/g, ' ').replace(/\s+/g, ' ').trim();
const hasKw = (s, kws) => {
    const n = norm(s);
    return !!n && kws.some((k) => n.includes(norm(k)));
};

// ── Word lists the rules read ─────────────────────────────────────────────
// Safety vendors: everything they sell the job is safety gear or training.
// (Not Xtreme Safety: besides fall protection under 56500 it sells spill
// berms and spill kits, which are containment consumables.)
const SAFETY_VENDORS = ['gosafe', 'magid glove', 'health & safety council'];
// Safety items by description, whatever cost type they posted under — the
// 9/25 rubric names hard hats, safety vests and gloves outright; FR, safety
// glasses and respirators are the same worn PPE. Ear plugs, fire
// extinguishers and first aid kits bought as consumables are NOT here:
// Steven Horman marked them billable in his 9/25 consumables review.
const SAFETY_KW = [
    'hard hat', 'hardhat', 'safety vest', 'hi vis', 'vest', 'glove', 'glv', 'safety glass',
    'respirator', 'coverall', 'gosafe',
];
// Gas monitors — on the rate sheet as "4 Gas Monitor", $8.00 a day, so they
// bill by the day (HeavyJob equipment code 4-GAS-MON), never by the purchase.
const MONITOR_KW = ['4 gas', '4gas', 'four gas', 'gas monitor', 'gas detector', 'h2s monitor', 'gx3r'];
// Hotels, airlines and rental cars that land outside the 55xxx travel types
// (P-card charges coded to Miscellaneous, say).
const TRAVEL_KW = [
    'doubletree', 'hotel', 'marriott', 'hilton', 'hampton inn', 'springhill', 'towneplace', 'holiday inn',
    'la quinta', 'best western', 'motel', 'airbnb', 'airfare', 'airline', 'car rental', 'national car',
    'enterprise rent', 'hertz',
];
// Freight lines ride the PO they came on, under its cost type.
export const FREIGHT_KW = ['freight', 'deliver', 'shipping', 'drop ship', 'hotshot', 'hot shot'];
export const isFreightDs = (ds) => hasKw(ds, FREIGHT_KW);
// Strike's affiliates — named in the PB4 Service Order's affiliate clause.
const AFFILIATES = ['crossfire', 'pickett', 'flozone', 'mclean'];
// Power tools and specialty tools inside the consumables cost type.
const TOOL_KW = [
    'm18', 'm12', 'impact', 'grinder', 'drill', 'sawzall', 'band saw', 'circular saw', 'torque', 'wrench',
    'threader', 'bender', 'milwaukee', 'dewalt', 'makita', 'hilti', 'clicker',
];
// The project team the Service Order reimburses (Overhead Costs).
const TEAM_KW = [
    'project manager', 'project controls', 'construction manager', 'superintendent', 'general manager',
    'area manager', 'qa/qc', 'quality', 'hse', 'safety manager', 'safety specialist', 'director',
    'vice president', 'project coordinator', 'admin', 'timekeep', 'cost controller', 'scheduler', 'planner',
    'document control',
];
const WELFARE_KW = ['porta', 'restroom', 'toilet', 'sanitation', 'dumpster', 'trash', 'garbage', 'wash station', 'break trailer', 'office trailer'];

// ── The language behind the rules ─────────────────────────────────────────
// Quoted, so a line moved off the invoice can be defended from the page it
// came from. `MTG.*` quotes the 9/25 rubric; `EXB.*` MEPC Exhibit B; `SO.*`
// resolves per job to that job's Service Order (SO_TEXT below) — the two
// SOs say most of the same things, but not in the same words.
export const CLAUSE_TEXT = {
    'MTG.SAFETY': { ref: 'Safety', text: 'Safety — Not Billable.' },
    'MTG.PPE': { ref: 'Consumables', text: 'Consumables rows — Billable, Safety is not. Can’t bill for: hardhats, safety vests, gloves.' },
    'MTG.CONS': { ref: 'Consumables', text: 'Consumables rows (review with Berns) — Billable, Safety is not.' },
    'MTG.TRAVEL': { ref: 'Travel', text: 'Travel (Hotel, Plane, Food, Car) — NOT BILLABLE.' },
    'MTG.HOTEL': { ref: 'Per Diem', text: 'If people who DON’T get PD stay, we bill the hotel as 175 a day, even if we pay in excess of the actual hotel spend. Flagging: if people code hours AND don’t get PD AND are on the sign-in sheet.' },
    'MTG.FUEL': { ref: 'Fuel', text: 'Specialty rentals: use VG fuel for equipment that we use as rebill (cranes). Watch those 2 cranes, make sure we get reimbursed, otherwise bill at cost for rebill items — don’t use what’s on the invoice; across all vendors use the average weekly cost per the EIA Gulf Coast diesel index. Take the prior week of the price index and charge that as the cost for the fuel.' },
    'MTG.RENT': { ref: 'Fuel', text: 'Anything that comes from the 3rd party is billable, including oil, filters, parts and maintenance time.' },
    'MTG.MAINT': { ref: 'Equipment', text: 'Maintenance is on us, except for 3rd party rentals.' },
    'MTG.TOOLS': { ref: 'Tools', text: 'Create a list of specialty tools that need to be added to the rate sheet, including small tools rentals. Save on SharePoint.' },
    'MTG.FREIGHT': { ref: 'Freight', text: 'Rebill at COST on both Blitz & PB4. These are flagged at delivery on the PO line item. If the “item” that is delivered is NOT BILLABLE, then the freight is also not billable — unless it’s on the rate sheet: block out the other rates and ONLY SHOW the freight of the item.' },
    'MTG.MOB': { ref: 'Freight', text: 'We get to bill mob/demob of all equipment — internal / external.' },
    'MTG.STANDBY': { ref: 'Equipment', text: 'A minimum daily charge for all equipment assigned shall be five (5) hours or one half (1/2) of listed daily rates, whichever is greater (standby). PB4: standby hours from 5 to 4 for equipment; equipment on standby for 10 days in a row — reach out to the client, notify them and ask if they want us to de-mob it.' },

    'EXB.OE.C': { ref: 'Operating Expenses (C)', text: 'safety training, including initial and refresher HES&SR training that may be required for the Site or any Work Area;' },
    'EXB.OE.D': { ref: 'Operating Expenses (D)', text: 'drug and alcohol testing of Contractor’s Personnel, whether required or requested by Contractor or Company;' },
    'EXB.OE.F': { ref: 'Operating Expenses (F)', text: 'personal protective equipment (PPE) and training in the use of protective clothing, safety equipment, and environmental protective equipment;' },
    'EXB.OH.A': { ref: 'Overhead Cost (A)', text: 'corporate personnel, including corporate and Contractor management … and personnel engaged in general management and general administrative and office work, including those designated as overhead in Attachment 2 to this Exhibit B;' },
    'EXB.OH.D': { ref: 'Overhead Cost (D)', text: 'other direct costs such as reproduction, printing, and voice and data communications, including local and long-distance telephone service, cell phones, postage, and courier services;' },
    'EXB.OH.F': { ref: 'Overhead Cost (F)', text: 'all costs associated with the use of vehicles;' },
    'EXB.A1.A': { ref: 'Attachment 1, Items of Cost (A)', text: 'Safety equipment, including fire-resistant coveralls, hard hats, safety glasses, H2S monitors, steel-toed/electric boots, and any other PPE required for Contractor’s Personnel to meet statutory and Company standards.' },
    'EXB.A1.B': { ref: 'Attachment 1, Items of Cost (B)', text: 'Any safety awards, prizes, or costs associated with safety incentives, regardless of Company’s consent to such awards.' },
    'EXB.B4.2': { ref: '¶4.2 Contractor’s Equipment pricing', text: 'Contractor’s Equipment pricing is set forth in Attachment 3 to this Exhibit B – Equipment. Contractor’s Equipment rates must include the type of rate.' },
    'EXB.B5.1A': { ref: '¶5.1(A) Per Diem Coverage', text: 'Per Diem means an all-inclusive per-day amount agreed as a fixed fee in lieu of reimbursement for actual temporary lodging, meal allowance, and related incidental expenses, including ground transportation and PPE.' },
    'EXB.B6.5': { ref: '¶6.5', text: 'Any subcontracted work performed by a Contractor Affiliate will not receive any markup, and Contractor shall notify Company when Contractor subcontracts with a Contractor Affiliate.' },
    'EXB.B6.6B': { ref: '¶6.6(B) Freight', text: 'The Third-Party Materials Markup does not include transportation costs, which Company will reimburse at the actual invoiced amount from the supplier, without markup.' },
    'EXB.B6.6C': { ref: '¶6.6(C)', text: 'There will be no markup on Material and Equipment or Contractor’s Equipment provided by a Contractor Affiliate.' },
    PREC: { ref: '¶1.3.2 Order of precedence', text: 'for the purpose of interpretation, the priority of the documents shall be taken in the following sequence: 1.3.2.1 Article 1 through Article 26; 1.3.2.2 Exhibit A (Scope of Work); and 1.3.2.3 Service Order 1.3.2.4 all other Exhibits.' },
};
const SRC = { MTG: `${RUBRIC.name} ${RUBRIC.date}`, EXB: 'MEPC Exh. B', PREC: 'MEPC' };

// Each Service Order's own words for the clauses the rules cite. A key a
// Service Order doesn't have (Blitz has no affiliate clause — the MEPC's
// ¶6.5 covers it there) is simply not quoted for that job.
const SO_OPEX = 'For the sake of clarity, the following Operating Expenses listed in this Service Order are not part of the Contractor’s rates and will be reimbursed per this Service Order: welfare facilities such as toilets, washing facilities, drinking water, changing rooms, lockers, and rest facilities required to execute the Work; refresher HES&SR training that may be required for the Site or any Work Area (initial onboarding and safety training excluded); small tools, consumables, and supplies of sufficient type and quantity to ensure the efficient, productive performance of the Work in accordance with Good Industry Practice; any temporary facilities required for the Work, including offices, electricity, telephone, Wi-Fi, and trash disposal; and Site cleaning and garbage removal prior to leaving the Site; Site specific safety monitors (H2S or other).';
const SO_OH = 'For the sake of clarity, the following Overhead Costs listed in this Service Order are not part of Contractor’s rates and will be reimbursed in accordance with this Service Order: Medical personnel as approved by the Company; QA/QC manager, HES&SR field advisor, general manager, area manager, project manager, project controls, superintendent, and other related positions; all costs of providing office space and facilities at Contractor’s offices for Contractor’s Personnel …; administrative, timekeeping, and secretarial support, other than project administrative or secretarial personnel performing direct project work; all costs associated with the use of vehicles.';
const SO_EQINCL = 'Equipment rates are inclusive of maintenance, repair, fuel, oil, lube, taxes, insurance, depreciation, license fees, overhead and profit. Equipment rates are on a straight-time basis and are exclusive of operating labor.';
const SO_WX = 'Weather Delays and Work Stoppages: Partial workdays shall be billed as follows: inability to commence scheduled work due to conditions beyond Contractor’s control: actual hours worked or a minimum 2 hours per person; work commenced but suspended by Company within 4 hours: minimum 4 hours per person; work exceeding 4 hours: the greater of actual hours worked or 5 hours per person minimum.';
export const SO_TEXT = {
    blitz: {
        OPEX: { ref: 'Basis for Compensation — Operating Expenses', text: SO_OPEX },
        OH: { ref: 'Basis for Compensation — Overhead Costs', text: SO_OH },
        EQINCL: { ref: 'Equipment', text: SO_EQINCL },
        WX: { ref: 'Weather Delays and Work Stoppages', text: SO_WX },
        VEH: { ref: 'Equipment — vehicles', text: 'The following labor classifications which are working on the project will require a vehicle to be provided and billed at the corresponding rates: Senior Management, Superintendents (General, A/B, Assistant), Foreman (General, Craft, Labor), Safety Manager, Safety Specialists, Project Coordinator, and Project Admin Support Specialist (or equivalent). … To bill a vehicle a minimum 4 headcount is expected for Foreman and Labor, others are to be billed for time use at site.' },
        HAUL: { ref: 'Equipment — hauling', text: 'Rates apply for all time including mobilization, demobilization and move-arounds. Cost for third-party hauling shall be charged at cost.' },
        LOGI: { ref: 'Basis for Compensation — Materials', text: 'Materials: at actual cost plus twelve percent (12%) administrative and handling fee.' },
        STANDBY: { ref: 'Equipment — minimum daily charge', text: 'A minimum daily charge for all equipment assigned shall be five (5) hours or one half (1/2) of listed daily rates, whichever is greater. All equipment onsite shall receive at least the minimal daily charge for standby.' },
        RATE4GAS: { ref: 'Attachment B — Schedule of Equipment Rates (Small Tools)', text: '4 Gas Monitor — $8.00 per day.' },
        SOURCE: { ref: 'Equipment', text: 'Equipment rates shall apply to all Contractor-sourced equipment for which an equipment rate is provided in this rate sheet, regardless of how contractor chooses to source such equipment or whether or not such equipment is owned, leased or rented from a third-party. The cost billed to Company shall not exceed these listed rates.' },
        PD: { ref: 'Labor — Per Diem', text: 'Per diem: $175.00 per person per day (all craft), 7 days/week, for personnel requiring overnight accommodations … Personnel assigned to projects requiring a minimum of five (5) consecutive workdays, and otherwise eligible for per diem, shall receive per diem compensation for the sixth and seventh days regardless of hours worked.' },
    },
    pb4: {
        OPEX: { ref: 'Basis for Compensation — Other (Operating Expenses)', text: SO_OPEX.replace('listed in this Service Order are not part of the Contractor’s rates and will be reimbursed per this Service Order', 'listed below are not part of the Contractor’s rates and will be reimbursed per this Service Order') },
        OH: { ref: 'Basis for Compensation — Labor (Overhead Costs)', text: SO_OH },
        EQINCL: { ref: 'Basis for Compensation — Equipment', text: SO_EQINCL },
        WX: { ref: 'Weather Delays and Work Stoppages', text: SO_WX },
        VEH: { ref: 'Basis for Compensation — Equipment (vehicles)', text: 'The following labor classifications which are working on the project will require a vehicle to be provided and billed at the corresponding rates: Site Based Leadership, Superintendents (General, A/B, Assistant), Foreman (General, Craft, Labor), Safety Manager, Safety Specialists, Project Coordinator, and Project Admin Support Specialist (or equivalent). … To bill a vehicle a minimum four (4) headcount is expected for Foreman and Labor, others are to be billed for time use at site.' },
        HAUL: { ref: 'Basis for Compensation — Equipment (hauling)', text: 'Rates apply for all time including mobilization, demobilization and move-arounds. Cost for third-party hauling shall be charged at cost with no mark-up.' },
        LOGI: { ref: 'Basis for Compensation — Materials', text: 'Materials shall be billed at actual cost-plus twelve percent (12%) administrative and handling fee; logistics fees are not subject to this mark-up and shall be billed at cost.' },
        AFF: { ref: 'Labor; Cost Control Provisions — Affiliates', text: 'Affiliates of the Contractors shall be billed at the actual cost and is not subject to any mark up. … Any procurement of materials, equipment, or subcontracted services from an entity affiliated with Contractor (including but not limited to Crossfire, Pickett Systems, Flozone, or McLean’s) shall be disclosed to Company in advance and is subject to Company’s written consent … no mark up shall apply to Contractor Affiliates.' },
        STANDBY: { ref: 'Basis for Compensation — Equipment (minimum daily charge)', text: 'A minimum daily charge for all equipment assigned shall be four (4) hours of listed daily rates … Equipment that is on site will be charged per the minimum day rate or the actual hours used, whichever is greater. Any equipment not utilized and billed at the minimum daily charge for ten (10) business days shall be presented to Company for option to demobilization.' },
        RATE4GAS: { ref: 'Attachment B — Schedule of Equipment Rates (Small Tools)', text: '4 Gas Monitor — $8.00 per day.' },
        SOURCE: { ref: 'Basis for Compensation — Equipment', text: 'Equipment rates shall apply to all Contractor-sourced equipment for which an equipment rate is provided in this rate sheet, regardless of how contractor chooses to source such equipment or whether or not such equipment is owned, leased or rented from a third-party. The cost billed to Company shall not exceed these listed rates.' },
        PD: { ref: 'Basis for Compensation — Labor (Per diem)', text: 'Per diem: $175.00 per person per day (all craft), for all week days worked. Personnel assigned to projects requiring a minimum of six (6) consecutive workdays, and otherwise eligible for per diem, shall receive per diem compensation for the seventh (7) day regardless of hours worked.' },
    },
};

// One quote, resolved for a job: { key, kind: 'mtg'|'so'|'exb', src, ref, text }.
const quote = (key, profile) => {
    if (key.startsWith('SO.')) {
        const q = (SO_TEXT[profile.id] || {})[key.slice(3)];
        return q ? { key, kind: 'so', src: profile.so, ...q } : null;
    }
    const q = CLAUSE_TEXT[key];
    if (!q) return null;
    const kind = key.startsWith('MTG.') ? 'mtg' : 'exb';
    return { key, kind, src: key.startsWith('MTG.') ? SRC.MTG : key === 'PREC' ? SRC.PREC : SRC.EXB, ...q };
};

// The quotes behind a rule for a job, in reading order: the rubric, the
// Service Order, then Exhibit B. `so` stays for callers that only split
// Service Order language from the rest.
export const clauseTexts = (rule, jobKey) => {
    const profile = contractFor(jobKey);
    const order = { mtg: 0, so: 1, exb: 2 };
    return (rule?.refs || [])
        .map((k) => quote(k, profile))
        .filter((c) => c && c.text)
        .map((c) => ({ ...c, so: c.kind === 'so' }))
        .sort((a, b) => order[a.kind] - order[b.kind]);
};

// Rule fields — all optional, and every one present must match:
//   ct   cost type codes            ctp  cost type prefixes   xct  cost types it skips
//   cc   phase (cost code) codes    cls  cost class      xcls classes it skips
//   kw   keywords in the line description   xkw  keywords that rule it out
//   jde  keywords in the labor classification (labor lines)
//   sup  keywords in the supplier name
//   min  line cost at or above this amount (dollars)
//   max  line cost below this amount
//   any  a list of the same shapes — at least one must match as well
// `clause` is the short citation; {SO} becomes the job's Service Order.
// First match wins, so the catalog reads in precedence order: what comes off
// the invoice (gas monitor purchases ahead of the rest of safety, for their
// own reason), then what bills at cost, then what needs a call, then what
// the contract reimburses outright. A rule with `po` never matches a line on
// its own — pricing applies it across a PO (freight on a non-billable item).
export const CONTRACT_RULES = [
    // ── Off the invoice ────────────────────────────────────────────────────
    {
        // Seth, 9/25: gas monitors bill as the rate sheet's 4 Gas Monitor,
        // $8.00 a day — so buying one is buying our own equipment, and the
        // purchase never bills (the rate applies "regardless of how
        // contractor chooses to source such equipment"). Firm: a by-hand
        // pass-through tag doesn't lift it, or a monitor would bill twice.
        // The 125 bought on 70 went out on F00294114 / F00294169 at cost
        // before this rule existed.
        // Not the fleet usage rows (52100) — those ARE the $8/day billing.
        id: 'monitors', effect: 'nb', firm: true, xcls: ['Labor'], xct: ['52100'], kw: MONITOR_KW,
        label: 'Gas monitor purchase — bills $8/day off the rate sheet instead',
        clause: '{SO} — Attachment B (4 Gas Monitor $8.00/day); Equipment (rates apply however sourced)',
        refs: ['SO.RATE4GAS', 'SO.SOURCE', 'MTG.FREIGHT'],
        note: 'Log monitors in HeavyJob as equipment under 4-GAS-MON; their days rate-bill at $8.00 each. The purchase itself stays off the invoice.',
    },
    {
        // Steve Horman, 9/30: bonds and insurance premiums, permits, insurance
        // claims, taxes, accrued expense and warranty reserve are not billable.
        // Ahead of safety and the keyword rules so the cost type decides.
        id: 'corpcost', effect: 'nb', ct: ['56600', '56700', '56800', '57100', '57200', '57300'],
        label: 'Bonds, insurance, permits, claims, taxes, accruals and warranty reserve',
        clause: 'Not billable cost types (Steve Horman 9/30)',
        refs: [],
        note: 'Bonds and insurance premiums, permits, insurance claims, taxes, accrued expense and warranty reserve never bill.',
    },
    {
        id: 'safety', effect: 'nb', xcls: ['Labor'],
        any: [{ ct: ['56500'] }, { sup: SAFETY_VENDORS }, { kw: SAFETY_KW }],
        label: 'Safety and PPE',
        clause: '9/25 rubric — Safety; Exh. B ¶1 OE(F), Att. 1(A)',
        refs: ['MTG.SAFETY', 'MTG.PPE', 'EXB.OE.F', 'EXB.A1.A'],
        note: 'Hard hats, vests, gloves, FR, fall protection, first aid — none of it bills, whatever cost type it posted under. (Gas monitors bill by the day — see above.)',
    },
    {
        id: 'travel', effect: 'nb', xcls: ['Labor'],
        any: [{ ctp: ['55'] }, { kw: TRAVEL_KW }],
        label: 'Travel — hotel, airfare, meals, car rental',
        clause: '9/25 rubric — Travel; Exh. B ¶5.1(A)',
        refs: ['MTG.TRAVEL', 'MTG.HOTEL', 'EXB.B5.1A'],
        note: 'Not billable. Someone we put up without per diem bills as per diem at $175 a day — never the hotel bill.',
    },
    {
        id: 'training', effect: 'nb',
        any: [{ cc: ['100201'] }, { kw: ['360training', 'orientation', 'onboarding'], xcls: ['Labor'] }],
        label: 'Onboarding and initial safety training',
        clause: '{SO} — Operating Expenses (initial onboarding and safety training excluded)',
        refs: ['SO.OPEX', 'MTG.SAFETY', 'EXB.OE.C'],
        note: 'Initial onboarding and safety training stay inside the rates. Refresher HES&SR training the Site requires is reimbursable — mark those lines billable.',
    },
    {
        id: 'eqfuel', effect: 'nb', ct: ['52300', '52400'],
        label: 'Fuel, fluids and repairs for our own equipment',
        clause: '{SO} — Equipment (rates inclusive); 9/25 rubric — Fuel, Equipment',
        refs: ['MTG.MAINT', 'MTG.FUEL', 'SO.EQINCL'],
        note: 'Our equipment rates are wet and include maintenance. Fuel that went into a third-party rebill rental (the cranes) bills at cost — the gallons at the prior week’s EIA Gulf Coast diesel average, not the invoice price. Mark those lines billable.',
    },
    {
        id: 'datest', effect: 'nb', xcls: ['Labor'],
        kw: ['drug test', 'drug screen', 'alcohol test', 'd&a test', 'background check'],
        label: 'Drug and alcohol testing',
        clause: 'Exh. B ¶1 OE(D)',
        refs: ['EXB.OE.D'],
        note: 'Testing is inside the rates whether Strike or the Company asked for it.',
    },
    {
        id: 'awards', effect: 'nb', xcls: ['Labor'],
        kw: ['safety award', 'safety incentive', 'gift card', 'prize'],
        label: 'Safety awards and incentives',
        clause: 'Exh. B Att. 1(B)',
        refs: ['EXB.A1.B'],
        note: 'Awards and prizes never bill, even with Company consent. (A retention or market premium in someone’s wage is different — it bills through the labor formula.)',
    },
    // Burden, BYOD, truck pay and rig rent (513xx / 514xx / 516xx / 517xx)
    // sit inside billingPricing's NEVER_BILLED_CT_PREFIX, so no line under
    // them reaches the Transactions grid. The rules stay because they record
    // why that exclusion is right, and Rates & Rules lists those cost types
    // under them as Not billable (Seth, 9/30).
    {
        id: 'byod', effect: 'nb', ct: ['51400'],
        label: 'BYOD — phone and data allowance',
        clause: 'Exh. B ¶1 Overhead Cost (D)',
        refs: ['EXB.OH.D'],
        note: 'Cell phones are overhead; the Service Order only reimburses Site telephone and Wi-Fi.',
    },
    {
        id: 'opssupport', effect: 'nb', ct: ['51350'],
        label: 'Allocated ops support',
        clause: 'Exh. B ¶1 Overhead Cost (A)',
        refs: ['EXB.OH.A'],
        note: 'Corporate and general management allocations are overhead.',
    },
    {
        id: 'burden', effect: 'nb', ctp: ['513'],
        label: 'Labor burden — payroll taxes, insurance and benefits',
        clause: 'Rate sheet — labor bills base wage × the multiplier',
        refs: [],
        note: 'Burden is inside the labor rate’s multiplier, so it never bills on top of it.',
    },
    {
        id: 'truckpay', effect: 'nb', ctp: ['516'],
        label: 'Truck pay',
        clause: '{SO} — Equipment (vehicles bill at the rate sheet)',
        refs: ['SO.VEH', 'SO.OH'],
        note: 'The vehicle bills at its rate sheet rate; the allowance paid to the person who drives it does not.',
    },
    {
        id: 'rigpay', effect: 'nb', ctp: ['517'],
        label: 'Rig rent — welding rig pay',
        clause: '{SO} — Equipment; Exh. B ¶4.2',
        refs: ['SO.SOURCE', 'EXB.B4.2'],
        note: 'The rig bills at its equipment rate; the rig rent paid through payroll does not.',
    },
    {
        // Applied by billingPricing across a PO, never matched line by line.
        id: 'freightnb', effect: 'nb', po: true,
        label: 'Freight on a non-billable item',
        clause: '9/25 rubric — Freight',
        refs: ['MTG.FREIGHT'],
        note: 'Every item on the PO is off the invoice, so the freight that delivered it is too.',
    },
    // ── Billable at cost, no markup ────────────────────────────────────────
    {
        id: 'affiliate', effect: 'cost', sup: AFFILIATES,
        label: 'Strike affiliate — at cost, no markup',
        clause: '{SO} — Affiliates; Exh. B ¶6.5, ¶6.6(C)',
        refs: ['SO.AFF', 'EXB.B6.5', 'EXB.B6.6C'],
        note: 'Crossfire, Pickett Systems, Flozone and McLean’s bill at actual cost, disclosed to the Company in advance.',
    },
    {
        id: 'hauling', effect: 'cost', xcls: ['Labor'],
        any: [{ ct: ['54300'] }, { kw: ['demob', 'haul'], xkw: ['u haul', 'uhaul'] }],
        label: 'Mob/demob and hauling — at cost',
        clause: '9/25 rubric — Freight; {SO} — Equipment (third-party hauling at cost)',
        refs: ['MTG.MOB', 'SO.HAUL'],
        note: 'Mob/demob of all equipment bills, internal and external; third-party hauling at cost.',
    },
    {
        id: 'freight', effect: 'cost', xcls: ['Labor'], kw: FREIGHT_KW,
        label: 'Freight and delivery — rebill at cost',
        clause: '9/25 rubric — Freight; {SO} — Materials; Exh. B ¶6.6(B)',
        refs: ['MTG.FREIGHT', 'SO.LOGI', 'EXB.B6.6B'],
        note: 'At cost on both jobs. Freight follows the item: when everything on the PO is off the invoice, so is its freight.',
    },
    // ── Billable, needs a call ─────────────────────────────────────────────
    {
        id: 'rentalfuel', effect: 'warn', ct: ['52200'], kw: ['fuel', 'diesel', 'gallon'],
        label: 'Fuel on a rental invoice — reprice at the EIA index',
        clause: '9/25 rubric — Fuel',
        refs: ['MTG.FUEL'],
        note: 'Bill the gallons at the prior week’s EIA Gulf Coast diesel average, not the vendor’s price.',
    },
    {
        id: 'tools', effect: 'warn', xcls: ['Labor'],
        any: [{ ct: ['53300', '53310'] }, { ct: ['53500'], kw: TOOL_KW }],
        label: 'Tool — belongs on the rate sheet’s specialty tools list',
        clause: '9/25 rubric — Tools; {SO} — Operating Expenses',
        refs: ['MTG.TOOLS', 'SO.OPEX'],
        note: 'Bills at cost + 12% today. Specialty tools and small-tool rentals are going on the rate sheet — these lines are that list.',
    },
    {
        id: 'medical', effect: 'warn', cls: 'Labor', jde: ['medic', 'first aid', 'paramedic', 'nurse'],
        label: 'Medical personnel — billable as approved by the Company',
        clause: '{SO} — Overhead Costs',
        refs: ['SO.OH'],
        note: 'The Service Order reimburses medical personnel only as the Company approves them.',
    },
    // ── Billable — the contract reimburses it ──────────────────────────────
    {
        id: 'team', effect: 'bill', cls: 'Labor', jde: TEAM_KW,
        label: 'Project team and supervision',
        clause: '{SO} — Overhead Costs',
        refs: ['SO.OH', 'PREC'],
        note: 'PM, construction manager, project controls, superintendents, QA/QC, HSE and admin staff bill at their rate sheet class.',
    },
    {
        id: 'weather', effect: 'bill', cls: 'Labor', cc: ['101001'],
        label: 'Weather delay',
        clause: '{SO} — Weather Delays and Work Stoppages',
        refs: ['SO.WX'],
        note: 'Actual hours, with the 2 / 4 / 5 hour per-person minimums.',
    },
    {
        id: 'vehicles', effect: 'bill', ct: ['52100'],
        label: 'Vehicles and fleet equipment',
        clause: '{SO} — Overhead Costs (vehicles); Equipment',
        refs: ['SO.OH', 'SO.VEH', 'SO.STANDBY'],
        note: 'Rate sheet by classification — a foreman or labor truck needs a crew of four. Standby still earns the minimum daily charge.',
    },
    {
        id: 'rentals', effect: 'bill', ct: ['52200'],
        label: 'Third-party rentals — oil, filters, parts and maintenance included',
        clause: '9/25 rubric — Fuel, Equipment',
        refs: ['MTG.RENT', 'MTG.MAINT'],
        note: 'Everything on the rental vendor’s invoice bills; fuel on it is re-priced at the EIA index.',
    },
    {
        id: 'consumables', effect: 'bill', ct: ['53500'],
        label: 'Consumables and supplies',
        clause: '9/25 rubric — Consumables; {SO} — Operating Expenses',
        refs: ['MTG.CONS', 'SO.OPEX'],
        note: 'Billable — safety items inside them are not (see Safety and PPE).',
    },
    {
        id: 'tempfac', effect: 'bill',
        any: [{ ct: ['56100', '56300'] }, { kw: WELFARE_KW, xcls: ['Labor'] }],
        label: 'Temporary facilities, field office and welfare',
        clause: '{SO} — Operating Expenses; Overhead Costs (office space)',
        refs: ['SO.OPEX', 'SO.OH'],
        note: 'Office trailers, generators, tanks, storage, toilets, trash and site cleaning are reimbursed.',
    },
];

const condMatches = (r, line) => {
    const ct = String(line.ct || '');
    if (r.ct && !r.ct.includes(ct)) return false;
    if (r.ctp && !r.ctp.some((p) => ct.startsWith(p))) return false;
    if (r.xct && r.xct.includes(ct)) return false;
    if (r.cc && !r.cc.includes(String(line.cc || ''))) return false;
    if (r.cls && r.cls !== line.cls) return false;
    if (r.xcls && r.xcls.includes(line.cls)) return false;
    if (r.kw && !hasKw(line.ds, r.kw)) return false;
    if (r.xkw && hasKw(line.ds, r.xkw)) return false;
    if (r.jde && !hasKw(line.jde, r.jde)) return false;
    if (r.sup && !hasKw(line.sup, r.sup)) return false;
    const c = Math.abs(Number(line.cost) || 0);
    if (r.min != null && c < r.min) return false;
    if (r.max != null && c >= r.max) return false;
    return true;
};
const ruleMatches = (r, line) => !r.po && condMatches(r, line)
    && (!r.any || r.any.some((sub) => condMatches(sub, line)));

// The catalog with each rule's citation resolved for a contract profile —
// built once per profile, so every line on a job shares the same objects.
const resolved = {};
export const rulesFor = (jobKey) => {
    const p = contractFor(jobKey);
    if (!resolved[p.id]) {
        resolved[p.id] = CONTRACT_RULES.map((r) => ({ ...r, clause: r.clause.replace(/\{SO\}/g, p.soShort) }));
    }
    return resolved[p.id];
};
export const contractRuleById = (id, jobKey) => rulesFor(jobKey).find((r) => r.id === id) || null;

/**
 * The contract rule a cost line falls under, or null.
 *
 * line: { ct, cc, ds, cls, jde, sup, cost, pt }
 * `pt` true means someone tagged the line pass-through BY HAND on
 * Transactions — a deliberate "the client buys this through us" call, which
 * outranks a not-billable rule — unless the rule is `firm` (a gas monitor
 * purchase: the monitor bills by the day, so billing the purchase too would
 * bill it twice). Rule-tagged pass-through lines are never exempt: freight
 * on an item nobody can bill is not billable either.
 *
 * First match wins — CONTRACT_RULES is in precedence order.
 */
export const contractRule = (line, on = true, jobKey) => {
    if (!on || !line) return null;
    const hit = rulesFor(jobKey).find((r) => ruleMatches(r, line)) || null;
    if (hit && hit.effect === 'nb' && line.pt && !hit.firm) return null;
    return hit;
};

// What a rule's effect means in words, used everywhere the effect is shown —
// the Cost Report section, Rates & Rules, both exports. 'warn' cost is still
// ON the invoice: the rule flagged it for a call, it did not take it off, so
// the label leads with the billable state and the action follows it.
export const EFFECT_LABEL = {
    nb: 'Not billable',
    cost: 'Billable at cost',
    warn: 'Billable — needs review',
    bill: 'Billable',
};
export const effectLabel = (effect) => EFFECT_LABEL[effect] || EFFECT_LABEL.warn;
// Screen colors per effect: [text, background].
export const EFFECT_TONE = {
    nb: ['#475569', '#f1f5f9'],
    cost: ['#1e40af', '#dbeafe'],
    warn: ['#b45309', '#fef3c7'],
    bill: ['#166534', '#dcfce7'],
};
const EFFECT_ORDER = { nb: 0, cost: 1, warn: 2, bill: 3 };

// The terms of the job's contract that aren't a yes/no on a single cost line
// — per diem, standby minimums, fuel pricing, freight — for Rates & Rules
// and the Cost Report, and handed to the pre-export assistant. `how` says
// where the app applies it, when it does.
export const contractTerms = (jobKey) => {
    const p = contractFor(jobKey);
    const pb4 = p.id === 'pb4';
    return [
        {
            k: 'Per diem',
            text: pb4
                ? '$175 per person per day worked. Six consecutive workdays earn the seventh.'
                : '$175 per person per day. Five consecutive workdays earn the sixth and seventh.',
            how: 'Per diem policy on Rates & Rules defaults to it; per diem bills at what payroll paid.',
        },
        {
            k: 'Hotels',
            text: 'Travel — hotel, airfare, meals, car — never bills. Someone we put up who draws no per diem bills as per diem at $175 a day, even when the hotel cost more.',
            how: 'Travel lines default Not Billable; labor QA lists who worked without per diem.',
        },
        {
            k: 'Equipment standby',
            text: `Minimum daily charge for all equipment assigned: ${p.standbyText}.${p.standbyDays ? ` Equipment on standby ${p.standbyDays} business days in a row — tell the client and ask whether to demob it (mark it standby in HeavyJob).` : ''}`,
            how: 'Pre-export checks flag unit-days billed under the minimum.',
        },
        {
            k: 'Fuel',
            text: 'Our equipment rates are wet — fuel, oil and maintenance are inside them. Third-party rebill rentals (the cranes): VoltaGrid fuels them, otherwise the fuel bills at cost — gallons at the prior week’s EIA Gulf Coast diesel average, never the invoice price. Oil, filters, parts and maintenance time from the rental vendor bill.',
            how: 'Fuel/repair lines default Not Billable; rental invoice fuel is flagged.',
        },
        {
            k: 'Gas monitors',
            text: '$8.00 per monitor per day off the rate sheet (4 Gas Monitor). The purchase never bills — the day rate is how the monitor is recovered.',
            how: 'Monitor purchases default Not Billable; log monitors in HeavyJob under 4-GAS-MON and their days rate-bill through the equipment tier map.',
        },
        {
            k: 'Freight',
            text: 'Rebills at cost. Freight on an item that isn’t billable isn’t billable either. Mob/demob of all equipment bills — internal and external; third-party hauling at cost.',
            how: 'Freight and hauling price at 0%; freight on an all-non-billable PO defaults Not Billable.',
        },
        {
            k: 'Markups',
            text: `Labor at actual base wage × 1.633 (OT 1.4× ST). Materials, third-party services and equipment not on the rate sheet at cost + 12%${pb4 ? '; logistics at cost' : ''}. Strike affiliates (Crossfire, Pickett Systems, Flozone, McLean’s) at cost, no markup.`,
            how: 'Affiliate lines price at 0%.',
        },
        {
            k: 'Overtime',
            text: pb4
                ? 'Over 40 hours a week and recognized holidays. OT hours are agreed each week before the schedule is released, with prior written Company approval.'
                : 'Over 40 hours a week and recognized holidays.',
        },
        {
            k: 'Tools',
            text: 'Specialty tools and small-tool rentals go on the rate sheet — keep the list on SharePoint.',
            how: 'Tool lines carry a review flag until then.',
        },
        ...(pb4 ? [{
            k: 'Invoicing',
            text: 'Biweekly, backed by signed labor and equipment reports. 5% of the $59,587,811 estimate ($2,979,390.55) invoices at mobilization. No retainage. Out-of-scope work only with an approved Change Order.',
        }] : []),
    ];
};

// Cost lines grouped by the rule they matched — the Rates & Rules preview and
// the Cost Report's contract section. Billed lines are reported separately:
// their billing is already frozen, so a rule can't move them.
export const contractPreview = (txns, on = true, jobKey) => {
    const groups = {};
    const sums = { nb: 0, cost: 0, warn: 0, bill: 0 };
    let billedCost = 0;
    (txns || []).forEach((t) => {
        // Re-read every line against the rules (the Rates & Rules preview
        // runs before the toggle is saved). A priced line's pass-through
        // exemption is its by-hand tag (ptOv), not the rule-tagged `pt`; the
        // PO-level freight rule no single line can match rides in on `cr`.
        const r = t.cr && t.cr.po && on ? t.cr
            : contractRule(t.ptOv !== undefined ? { ...t, pt: t.ptOv === 'Y' } : t, on, jobKey);
        if (!r) return;
        const g = groups[r.id] || (groups[r.id] = { ...r, n: 0, cost: 0, billed: 0, billedCost: 0, lines: [], byCt: {} });
        g.n += 1;
        // The cost types a category reaches into — a keyword rule (safety,
        // travel, freight) catches lines under several.
        const ctk = String(t.ct || '');
        const c = g.byCt[ctk] || (g.byCt[ctk] = { ct: ctk, desc: t.ctDesc || '', n: 0, cost: 0 });
        c.n += 1;
        c.cost += Number(t.cost) || 0;
        if (!c.desc && t.ctDesc) c.desc = t.ctDesc;
        // The lines themselves, so the Cost Report can open a category and
        // show what is actually in it — biggest cost first.
        g.lines.push(t);
        g.cost += Number(t.cost) || 0;
        if (t.inv) { g.billed += 1; g.billedCost += Number(t.cost) || 0; billedCost += Number(t.cost) || 0; }
        sums[r.effect] = (sums[r.effect] || 0) + (Number(t.cost) || 0);
    });
    Object.values(groups).forEach((g) => {
        g.lines.sort((a, b) => Math.abs(Number(b.cost) || 0) - Math.abs(Number(a.cost) || 0));
        g.cts = Object.values(g.byCt).sort((a, b) => Math.abs(b.cost) - Math.abs(a.cost) || a.ct.localeCompare(b.ct));
        delete g.byCt;
    });
    const list = Object.values(groups).sort((a, b) => (
        a.effect === b.effect ? b.cost - a.cost : EFFECT_ORDER[a.effect] - EFFECT_ORDER[b.effect]));
    return {
        groups: list,
        nbCost: sums.nb,
        costCost: sums.cost,
        warnCost: sums.warn,
        billCost: sums.bill,
        billedCost,
        n: list.reduce((a, g) => a + g.n, 0),
    };
};
